<template>
  <div class="blissrose-video">
    <!-- 视频源配置 -->
    <el-card shadow="never" class="config-card">
      <div slot="header" class="card-header">
        <span>视频源</span>
        <div class="card-header-right">
          <el-tag :type="stateTag" size="small" class="state-tag">{{ stateText }}</el-tag>
          <el-button size="small" type="primary" :loading="saving" @click="saveConfig">保存并应用</el-button>
        </div>
      </div>

      <el-form :inline="true" size="small">
        <el-form-item label="源类型">
          <el-radio-group v-model="sourceType" @change="onSourceTypeChange">
            <el-radio-button label="dataset">数据集版本</el-radio-button>
            <el-radio-button label="manual">手填路径</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <template v-if="sourceType === 'dataset'">
          <el-form-item label="版本">
            <el-select v-model="datasetRef" class="dataset-select" placeholder="选择数据集版本" @change="onDatasetChange">
              <el-option
                v-for="item in datasets"
                :key="item.ref"
                :label="datasetLabel(item)"
                :value="item.ref"
                :disabled="!isDatasetReady(item)"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="划分">
            <el-select v-model="datasetSplit" class="split-select" placeholder="选择划分" @change="applyDatasetSource">
              <el-option
                v-for="item in splitOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
                :disabled="item.disabled"
              />
            </el-select>
          </el-form-item>
        </template>
        <el-form-item v-else label="源地址">
          <el-input
            v-model="form.source"
            class="source-input"
            placeholder="摄像头索引 / 图片目录 / RTSP 地址"
            @keyup.enter.native="saveConfig"
          />
        </el-form-item>
        <el-form-item label="推理实例">
          <el-select v-model="form.instance_id" class="instance-select" placeholder="选择实例">
            <el-option v-for="item in instances" :key="item.id" :label="instanceLabel(item)" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="限帧">
          <el-input-number v-model="form.fps_limit" :min="1" :max="60" controls-position="right" />
        </el-form-item>
        <el-form-item label="隔帧">
          <el-input-number v-model="form.skip_frames" :min="0" :max="30" controls-position="right" />
        </el-form-item>
        <el-form-item v-if="truthToggleVisible" label="真值叠加">
          <el-switch v-model="form.show_gt" />
        </el-form-item>
      </el-form>

      <div v-if="sourceType === 'dataset' && form.source" class="hint">当前源：{{ form.source }}</div>
      <div v-if="datasetHint" class="hint warn">{{ datasetHint }}</div>
      <div class="hint">配置改动在重新开始拉流后生效；真值叠加开关即时生效；限帧是每秒最多处理多少帧，隔帧是每处理一帧跳过多少帧。</div>
    </el-card>

    <el-row :gutter="12">
      <!-- 播放器 -->
      <el-col :xs="24" :lg="16">
        <el-card shadow="never" class="player-card">
          <div slot="header" class="card-header">
            <span>实时画面</span>
            <span class="updated-at">{{ updatedAt ? `更新于 ${updatedAt}` : '尚未取到状态' }}</span>
          </div>

          <div class="player">
            <!-- 服务端按帧推送「图 + 检测结果」，这里只负责把这一帧画出来 -->
            <canvas v-if="streaming" ref="canvas" class="player-canvas" />
            <div v-else class="player-placeholder">
              <i class="el-icon-video-camera-solid"></i>
              <p>{{ placeholderText }}</p>
            </div>
          </div>

          <!-- 重连到阈值仍失败就停手 -->
          <el-alert v-if="streamBroken" type="error" show-icon :closable="false" class="notice">
            <template slot="title">画面连接已断开</template>
            <div>连续 {{ reconnectTolerance }} 次重连都没接上，停止后重新开始拉流即可。</div>
          </el-alert>

          <div class="transport">
            <el-button
              v-if="!running"
              type="primary"
              size="small"
              icon="el-icon-video-play"
              :loading="acting"
              @click="handleStart"
            >开始拉流</el-button>
            <el-button
              v-else
              type="danger"
              size="small"
              icon="el-icon-switch-button"
              :loading="acting"
              @click="handleStop"
            >停止</el-button>
            <el-button size="small" :disabled="!running || acting" @click="handlePause">
              {{ paused ? '继续' : '暂停' }}
            </el-button>

            <span class="speed-label">倍速</span>
            <el-radio-group v-model="speed" size="mini" :disabled="!running" @change="handleSpeed">
              <el-radio-button :label="0.5">0.5x</el-radio-button>
              <el-radio-button :label="1">1x</el-radio-button>
              <el-radio-button :label="2">2x</el-radio-button>
              <el-radio-button :label="4">4x</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 只有图片目录源有总帧数，其余源拿不到进度 -->
          <div v-if="totalFrames > 0" class="seek">
            <span class="seek-label">进度</span>
            <!-- 拖动期间屏蔽轮询回写 -->
            <div class="seek-slider" @mousedown="onSeekStart">
              <el-slider
                v-model="seekFrame"
                :min="0"
                :max="totalFrames - 1"
                :disabled="!running"
                :show-tooltip="false"
                @change="handleSeek"
              />
            </div>
            <span class="seek-value">{{ frameIndex }} / {{ totalFrames }}</span>
          </div>
        </el-card>
      </el-col>

      <!-- 检测结果 -->
      <el-col :xs="24" :lg="8">
        <el-card shadow="never" class="stats-card">
          <div slot="header" class="card-header">
            <span>检测结果</span>
          </div>

          <div class="summary-grid">
            <div v-for="item in summaryItems" :key="item.label" class="summary-item">
              <div class="summary-value">{{ item.value }}</div>
              <div class="summary-label">{{ item.label }}</div>
            </div>
          </div>

          <el-table v-if="detections.length" :data="detections" size="mini" class="detection-table">
            <el-table-column prop="class_name" label="类别" show-overflow-tooltip />
            <el-table-column label="置信度" width="80" align="right">
              <template slot-scope="scope">{{ confidenceText(scope.row.confidence) }}</template>
            </el-table-column>
          </el-table>
          <div v-else class="detection-empty">当前帧没有检出目标</div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { fetchInferenceStatus } from '../../api/blissrose/inference';
import {
  fetchVideoConfig,
  updateVideoConfig,
  fetchVideoStatus,
  startVideo,
  stopVideo,
  pauseVideo,
  setVideoSpeed,
  seekVideo,
  getVideoStreamUrl,
} from '../../api/blissrose/video';
import { INSTANCE_STATES } from '../../utils/labels';
import { fetchTrainDatasets } from '../../api/blissrose/training';

// 拉流状态轮询；获取失败到阈值就停
const POLL_INTERVAL_MS = 1000;
const FAIL_TOLERANCE = 3;
// 断流后浏览器自动重连；连续失败到上限就停手
const RECONNECT_TOLERANCE = 3;

/** 运行状态到展示文案与标签配色 */
const STATE_TEXT = { running: '运行中', stopped: '已停止', error: '异常' };
const STATE_TAG = { running: 'success', stopped: 'info', error: 'danger' };

/** 数据集版本的划分顺序：测试集优先 */
const DATASET_SPLITS = [
  { value: 'test', label: '测试' },
  { value: 'val', label: '验证' },
  { value: 'train', label: '训练' },
];

export default {
  name: 'VideoStream',

  data() {
    return {
      loading: false,
      saving: false,
      acting: false,
      instances: [],
      datasets: [],
      sourceType: 'manual',
      datasetRef: '',
      datasetSplit: '',
      form: {
        source: '',
        instance_id: '',
        fps_limit: 15,
        skip_frames: 0,
        show_gt: true,
      },
      state: 'stopped',
      fps: 0,
      result: null,
      frameIndex: 0,
      totalFrames: 0,
      paused: false,
      speed: 1,
      seekFrame: 0,
      dragging: false,
      updatedAt: '',
      streamBroken: false,
      reconnectCount: 0,
      reconnectTolerance: RECONNECT_TOLERANCE,
      failCount: 0,
      pollPaused: false,
      failTolerance: FAIL_TOLERANCE,
      pollTimer: null,
      // SSE 侧：常连接、已渲染帧号与最近一张位图（窗口尺寸变化时重画）
      eventSource: null,
      frameVersion: 0,
      renderedVersion: 0,
      sseEpoch: '',
      bitmap: null,
      pendingFrame: null,
    };
  },

  computed: {
    running() {
      return this.state === 'running';
    },

    /** 只有运行中才挂播放器（停止时不留画面） */
    streaming() {
      return this.running && !this.streamBroken;
    },

    stateText() {
      return STATE_TEXT[this.state] || this.state;
    },

    stateTag() {
      return STATE_TAG[this.state] || 'info';
    },

    placeholderText() {
      if (this.state === 'error') return '拉流异常，请检查推理实例';
      return '未开始拉流';
    },

    /** 源落在数据集物化布局（…/images/<划分>）内时展示真值开关 */
    truthToggleVisible() {
      if (this.sourceType === 'dataset') return true;
      return /(^|\/)images\/(train|val|test)\/?$/.test(this.form.source || '');
    },

    currentDataset() {
      return this.datasets.find(item => item.ref === this.datasetRef) || null;
    },

    /** 选中版本的划分选项：帧数 0 的置灰 */
    splitOptions() {
      const item = this.currentDataset;
      if (!item) return [];
      const counts = { test: item.test_count, val: item.val_count, train: item.train_count };
      return DATASET_SPLITS.map(split => ({
        value: split.value,
        label: `${split.label}（${counts[split.value] || 0}）`,
        disabled: !counts[split.value],
      }));
    },

    /** 选中版本缺少测试划分时的提醒 */
    datasetHint() {
      const item = this.currentDataset;
      if (this.sourceType !== 'dataset' || !item || item.test_count) return '';
      return `「${item.ref}」没有测试划分，可选验证或训练划分。`;
    },

    /** 置信度高的排前面 */
    detections() {
      const list = (this.result && this.result.detections) || [];
      return list.slice().sort((a, b) => b.confidence - a.confidence);
    },

    summaryItems() {
      const has = this.result || {};
      const gtCount = has.gt_count;
      const withGt = gtCount !== null && gtCount !== undefined;
      return [
        { label: '帧率', value: this.fps ? `${Number(this.fps).toFixed(1)} fps` : '-' },
        { label: '推理耗时', value: has.inference_ms ? `${Math.round(has.inference_ms)} ms` : '-' },
        withGt
          ? { label: '模型 / 真值', value: `${has.count || 0} / ${gtCount}` }
          : { label: '目标数', value: String(has.count || 0) },
      ];
    },
  },

  watch: {
    /** 画布随「运行中」挂上；挂载后先把手里最近一帧补画出来 */
    streaming(value) {
      if (value) this.$nextTick(() => this.paintFrame(this.bitmap));
    },
  },

  mounted() {
    this.loadConfig();
    this.loadStatus();
    this.startPoll();
    window.addEventListener('resize', this.onWindowResize);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },

  beforeDestroy() {
    this.stopPoll();
    this.closeStream();
    this.releaseBitmap();
    this.onSeekEnd();
    window.removeEventListener('resize', this.onWindowResize);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },

  methods: {
    async loadConfig() {
      // 先拿实例与数据集清单：判断存下来的实例还在不在、源属于哪个版本
      const gotInstances = await this.loadInstances();
      await this.loadDatasets();
      try {
        const { data } = await fetchVideoConfig();
        this.form = {
          source: data.source,
          instance_id: data.instance_id,
          fps_limit: data.fps_limit,
          skip_frames: data.skip_frames,
          show_gt: data.show_gt !== false,
        };
        this.syncSourceMode(data.source || '');
        // 存的是内置实例或已删除的实例：清掉让重选
        const saved = this.form.instance_id;
        if (gotInstances && saved && !this.instances.some(item => item.id === saved)) {
          this.form.instance_id = '';
          this.$message.warning('原推理实例不可用，请重新选择');
        }
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      }
    },

    /** 拉实例列表，成功返回 true；内置实例不进下拉 */
    async loadInstances() {
      try {
        const { data } = await fetchInferenceStatus();
        const { instances, defaultInstanceId } = data;
        this.instances = (instances || []).filter(item => item.id !== defaultInstanceId);
        return true;
      } catch (err) {
        // 错误提示已由拦截器处理
        return false;
      }
    },

    /** 拉数据集版本清单：版本 × 划分选择器与源路径都由它拼出 */
    async loadDatasets() {
      try {
        const { data } = await fetchTrainDatasets();
        this.datasets = (data && data.items) || [];
      } catch (err) {
        // 错误提示已由拦截器处理；清单为空时源走手填模式
        this.datasets = [];
      }
    },

    /** 已保存的源反解回「数据集版本 × 划分」或手填模式 */
    syncSourceMode(source) {
      const clean = String(source || '').replace(/\/+$/, '');
      for (const item of this.datasets) {
        if (!item.cache_path) continue;
        const hit = DATASET_SPLITS.find(split => clean === `${item.cache_path}/images/${split.value}`);
        if (hit) {
          this.sourceType = 'dataset';
          this.datasetRef = item.ref;
          this.datasetSplit = hit.value;
          return;
        }
      }
      this.sourceType = 'manual';
    },

    /** 切回数据集模式：按当前版本×划分重建源路径；没有可用版本就取第一个已就绪的 */
    onSourceTypeChange(type) {
      if (type !== 'dataset') return;
      if (!this.currentDataset) {
        const first = this.datasets.find(item => this.isDatasetReady(item));
        if (!first) return;
        this.datasetRef = first.ref;
      }
      this.onDatasetChange();
    },

    /** 换版本：保留仍可用的划分，否则取第一个有帧数的（测试优先） */
    onDatasetChange() {
      if (!this.currentDataset) return;
      const available = this.splitOptions.filter(item => !item.disabled);
      if (!available.some(item => item.value === this.datasetSplit)) {
        this.datasetSplit = available.length ? available[0].value : '';
      }
      this.applyDatasetSource();
    },

    /** 版本 × 划分拼出容器内源路径 */
    applyDatasetSource() {
      const item = this.currentDataset;
      if (!item || !item.cache_path || !this.datasetSplit) return;
      this.form.source = `${item.cache_path}/images/${this.datasetSplit}`;
    },

    isDatasetReady(item) {
      return item.cache_state === 'ready';
    },

    /** 版本下拉一行：名@版本＋帧数＋未就绪标识 */
    datasetLabel(item) {
      const info = item.frame_count ? `（${item.frame_count} 帧）` : '';
      const mark = this.isDatasetReady(item) ? '' : ' · 未就绪';
      return `${item.ref}${info}${mark}`;
    },

    /** 把表单配置写回服务端，成功返回 true；只落库回填，不弹提示 */
    async persistConfig() {
      if (!this.form.source) {
        this.$message.warning(this.sourceType === 'dataset' ? '请先选择数据集版本与划分' : '源地址不能为空');
        return false;
      }
      if (!this.form.instance_id) {
        // 没选实例就没得存；下拉为空说明一个实例都没有
        const hint = this.instances.length
          ? '请先在上方选择推理实例'
          : '暂无推理实例，请先到模型实例页新建并启动';
        this.$message.warning(hint);
        return false;
      }
      this.saving = true;
      try {
        const { data } = await updateVideoConfig({ ...this.form });
        this.form = {
          source: data.source,
          instance_id: data.instance_id,
          fps_limit: data.fps_limit,
          skip_frames: data.skip_frames,
          show_gt: data.show_gt !== false,
        };
        return true;
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
        return false;
      } finally {
        this.saving = false;
      }
    },

    /** 保存视频源配置；不自动重启拉流 */
    async saveConfig() {
      if (await this.persistConfig()) {
        this.$message.success('视频源已保存；真值叠加即时生效，其余改动重新开始拉流后生效');
      }
    },

    /** 开始拉流；先落配置再启动（服务端校验已保存的实例） */
    async handleStart() {
      if (!await this.persistConfig()) return;
      if (!await this.ensureInstanceReady()) return;
      this.acting = true;
      try {
        await startVideo();
        // 立刻回读一次，不等下一拍轮询
        this.resetStream();
        await this.loadStatus();
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.acting = false;
      }
    },

    /** 选中的实例没进显存就地拦下 */
    async ensureInstanceReady() {
      await this.loadInstances();
      const hit = this.instances.find(item => item.id === this.form.instance_id);
      if (!hit) {
        // 没选或实例已被删除；服务端同样会拦下
        this.$message.warning('请先在上方选择一个已启动的推理实例');
        return false;
      }
      if (hit.state === 'ready') return true;
      const stateText = (INSTANCE_STATES[hit.state] || {}).label || hit.state || '未知';
      this.$message.warning(`实例「${hit.name}」当前状态是「${stateText}」，请先到模型实例页启动它`);
      return false;
    },

    async handleStop() {
      this.acting = true;
      try {
        await stopVideo();
        this.closeStream();
        await this.loadStatus();
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.acting = false;
      }
    },

    async handlePause() {
      this.acting = true;
      try {
        await pauseVideo(!this.paused);
        await this.loadStatus();
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.acting = false;
      }
    },

    async handleSpeed(speed) {
      try {
        await setVideoSpeed(speed);
      } catch (err) {
        // 失败时回读服务端状态退回倍速
        await this.loadStatus();
      }
    },

    async handleSeek(frameIndex) {
      try {
        await seekVideo(frameIndex);
      } catch (err) {
        // 失败就回读真实进度
        await this.loadStatus();
      }
    },

    /** 按下开始拖动；松手监听挂在 window 上（滑到外面也能收到） */
    onSeekStart() {
      this.dragging = true;
      window.addEventListener('mouseup', this.onSeekEnd);
    },

    onSeekEnd() {
      this.dragging = false;
      window.removeEventListener('mouseup', this.onSeekEnd);
    },

    /** 重开拉流：重连 SSE 常连接 */
    resetStream() {
      this.reconnectCount = 0;
      this.streamBroken = false;
      // 上一场的最后一帧不带到新一场，等新帧到达再画
      this.releaseBitmap();
      this.pendingFrame = null;
      this.renderedVersion = 0;
      this.openStream();
    },

    /** 建立 SSE 常连接：每帧的图与检测结果在同一个事件里 */
    openStream() {
      this.closeStream();
      const source = new EventSource(
        getVideoStreamUrl({ since: this.frameVersion, epoch: this.sseEpoch }),
      );
      source.addEventListener('packet', this.onSsePacket);
      source.addEventListener('state', this.onSseState);
      source.onopen = this.onSseOpen;
      source.onerror = this.onSseError;
      this.eventSource = source;
    },

    closeStream() {
      const source = this.eventSource;
      if (!source) return;
      source.removeEventListener('packet', this.onSsePacket);
      source.removeEventListener('state', this.onSseState);
      source.onopen = null;
      source.onerror = null;
      source.close();
      this.eventSource = null;
    },

    onSseOpen() {
      this.reconnectCount = 0;
      this.streamBroken = false;
    },

    /** 浏览器会自动重连；连续失败到阈值就停手，交给用户重新开始拉流 */
    onSseError() {
      if (!this.running) return;
      this.reconnectCount += 1;
      if (this.reconnectCount >= RECONNECT_TOLERANCE) {
        this.streamBroken = true;
        this.closeStream();
      }
    },

    /** 状态事件兜住没有新帧的时段（停止、异常、到末帧） */
    onSseState(event) {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch (err) {
        return;
      }
      if (payload.epoch) this.sseEpoch = payload.epoch;
      this.state = payload.state || this.state;
      if (this.state !== 'running') this.closeStream();
    },

    /** 一个包同时更新图、表、帧号与指标：它们永远指向同一帧 */
    onSsePacket(event) {
      let packet;
      try {
        packet = JSON.parse(event.data);
      } catch (err) {
        return;
      }
      if (packet.epoch && packet.epoch !== this.sseEpoch) {
        // 换了一场会话：帧号从头算，旧画面留着等新帧覆盖
        this.sseEpoch = packet.epoch;
        this.frameVersion = 0;
        this.renderedVersion = 0;
      }
      if (typeof packet.v === 'number' && packet.v <= this.frameVersion) return;
      this.frameVersion = packet.v;
      this.state = packet.state || this.state;
      this.paused = !!packet.paused;
      this.speed = packet.speed || this.speed;
      this.fps = packet.fps;
      this.frameIndex = packet.frame_index;
      this.totalFrames = packet.total_frames;
      if (!this.dragging) this.seekFrame = packet.frame_index;
      this.result = {
        count: packet.count,
        detections: packet.detections || [],
        gt_count: packet.gt_count,
        inference_ms: packet.inference_ms,
      };
      this.updatedAt = dayjs().format('HH:mm:ss');
      this.reconnectCount = 0;
      this.streamBroken = false;
      if (packet.jpeg) this.renderFrame(packet.v, packet.jpeg);
    },

    /** 把一帧画到画布：等比内缩、居中、四周留黑，与 <img> 的 contain 表现一致 */
    async renderFrame(version, base64) {
      if (version < this.renderedVersion) return;
      // 标签页在后台时不解码，只留最新一帧，回到前台再画
      if (document.hidden) {
        this.pendingFrame = { version, base64 };
        return;
      }
      let bitmap;
      try {
        bitmap = await createImageBitmap(this.base64ToBlob(base64));
      } catch (err) {
        return;
      }
      // 解码期间可能已经有更新的帧画上去了，过期的不再覆盖
      if (version < this.renderedVersion) {
        bitmap.close();
        return;
      }
      if (this._isDestroyed) {
        bitmap.close();
        return;
      }
      this.renderedVersion = version;
      this.paintFrame(bitmap);
      this.releaseBitmap();
      this.bitmap = bitmap;
    },

    paintFrame(bitmap) {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const box = canvas.parentElement.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.round(box.width * dpr));
      const height = Math.max(1, Math.round(box.height * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#1f1f1f';
      ctx.fillRect(0, 0, width, height);
      if (!bitmap) return;
      const scale = Math.min(width / bitmap.width, height / bitmap.height);
      const drawWidth = bitmap.width * scale;
      const drawHeight = bitmap.height * scale;
      ctx.drawImage(bitmap, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
    },

    onWindowResize() {
      this.paintFrame(this.bitmap);
    },

    /** 回到前台补画后台期间留下的最新一帧 */
    onVisibilityChange() {
      if (document.hidden || !this.pendingFrame) return;
      const { version, base64 } = this.pendingFrame;
      this.pendingFrame = null;
      this.renderFrame(version, base64);
    },

    releaseBitmap() {
      if (this.bitmap && typeof this.bitmap.close === 'function') this.bitmap.close();
      this.bitmap = null;
    },

    base64ToBlob(base64) {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: 'image/jpeg' });
    },

    /** 先清再设，避免重复挂定时器 */
    startPoll() {
      this.stopPoll();
      this.pollTimer = setInterval(() => {
        // 上一次没回来就跳过这一拍
        if (!this.loading) this.loadStatus(true);
      }, POLL_INTERVAL_MS);
    },

    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    /** 拉取运行状态；silent 为定时轮询，不显示加载态 */
    async loadStatus(silent = false) {
      if (!silent) this.loading = true;
      try {
        const { data } = await fetchVideoStatus();
        // 运行时帧号、结果、进度都由包驱动；轮询只同步状态，避免把旧结果写回表格
        const packetDriven = data.state === 'running';
        this.state = data.state;
        this.paused = data.paused;
        this.speed = data.speed;
        if (!packetDriven) {
          this.fps = data.fps;
          this.result = data.result;
          this.frameIndex = data.frame_index;
          this.totalFrames = data.total_frames;
          // 拖动中不覆盖用户手上的位置
          if (!this.dragging) this.seekFrame = data.frame_index;
        }
        this.updatedAt = dayjs().format('HH:mm:ss');
        this.failCount = 0;
      } catch (err) {
        // 错误提示由响应拦截器统一弹出；计数到阈值后停轮询
        this.failCount += 1;
        if (this.failCount >= FAIL_TOLERANCE) {
          this.pollPaused = true;
          this.stopPoll();
        }
      } finally {
        if (!silent) this.loading = false;
      }
    },

    confidenceText(value) {
      return `${(Number(value) * 100).toFixed(1)}%`;
    },

    /** 下拉带上实例状态 */
    instanceLabel(item) {
      const stateText = (INSTANCE_STATES[item.state] || {}).label;
      return stateText ? `${item.name}（${stateText}）` : item.name;
    },
  },
};
</script>

<style scoped>
.config-card,
.player-card,
.stats-card {
  margin-bottom: 12px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header-right {
  display: flex;
  align-items: center;
}

.state-tag {
  margin-right: 12px;
}

.updated-at {
  font-size: 12px;
  color: #909399;
}

.source-input {
  width: 300px;
}

.instance-select {
  width: 160px;
}

.dataset-select {
  width: 220px;
}

.split-select {
  width: 120px;
}

.hint {
  font-size: 12px;
  line-height: 20px;
  color: #909399;
}

.hint.warn {
  color: #e6a23c;
}

.mono {
  padding: 0 4px;
  border-radius: 2px;
  background: #f5f7fa;
  font-family: Consolas, Monaco, monospace;
}

.player {
  display: flex;
  align-items: center;
  justify-content: center;
  /* 播放器尺寸固定，帧按比例内缩 */
  height: 560px;
  background: #1f1f1f;
  border-radius: 4px;
  overflow: hidden;
}

/* 画布铺满播放器；等比内缩与留黑边由绘制时换算，等价于 object-fit: contain */
.player-canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.player-placeholder {
  text-align: center;
  color: #909399;
}

.player-placeholder i {
  font-size: 48px;
}

.player-placeholder p {
  margin-top: 8px;
  font-size: 13px;
}

.notice {
  margin-top: 12px;
}

.transport {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 12px;
}

.speed-label {
  margin: 0 8px 0 16px;
  font-size: 12px;
  color: #606266;
}

.seek {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.seek-label {
  margin-right: 8px;
  font-size: 12px;
  color: #606266;
}

.seek-slider {
  flex: 1;
}

.seek-value {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
}

.summary-grid {
  display: flex;
  flex-wrap: wrap;
}

.summary-item {
  flex: 1 1 30%;
  padding: 4px 0;
}

.summary-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.summary-label {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.detection-table {
  margin-top: 12px;
  border-top: 1px solid #ebeef5;
}

.detection-empty {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
  font-size: 12px;
  color: #909399;
}
</style>
